import { GUIDANCE_KEY_PATTERN } from '@bookora/shared';

import { isTourKey } from './tours/registry';
import type { TourKey } from './tours/types';

/** The one deep-link grammar: `?help=hub | tour:<key>[.<stepId>] | tip:<itemKey>`. */
export type HelpLink =
  { kind: 'hub' } | { kind: 'tour'; key: TourKey; stepId?: string } | { kind: 'tip'; id: string };

const STEP_ID = /^[a-z0-9-]+$/;

/**
 * Pure. Anything malformed or unknown is `null` — ignored silently, no toast,
 * no request (a stale bell link must not produce an error).
 */
export function parseHelpParam(value: string | null | undefined): HelpLink | null {
  if (!value) return null;
  const raw = value.trim();
  if (raw === 'hub') return { kind: 'hub' };

  if (raw.startsWith('tour:')) {
    const rest = raw.slice('tour:'.length);
    const dot = rest.indexOf('.');
    const key = dot === -1 ? rest : rest.slice(0, dot);
    const stepId = dot === -1 ? undefined : rest.slice(dot + 1);
    if (!isTourKey(key)) return null;
    if (stepId !== undefined && !STEP_ID.test(stepId)) return null;
    return stepId ? { kind: 'tour', key, stepId } : { kind: 'tour', key };
  }

  if (raw.startsWith('tip:')) {
    const id = raw.slice('tip:'.length);
    // `ruleKey` or `ruleKey.instance` (the guide's item key).
    if (!GUIDANCE_KEY_PATTERN.test(id) || id.length > 105) return null;
    return { kind: 'tip', id };
  }
  return null;
}

/** What a `?help=` link opens: the hub, a tour (optionally at a step), or a tip. */
export type HelpTarget = 'hub' | { tour: TourKey; step?: string } | { tip: string };

/**
 * The one producer of `?help=` links (the inverse of `parseHelpParam`):
 * `helpHref('/calendar', { tour: 'calendar' })` → `/calendar?help=tour:calendar`.
 * Keeps the path's own query and hash, and replaces an existing `help`. A link
 * to a tour that has no steps yet (a planned stub) is still produced; the
 * handler ignores it until the tour lands.
 *
 * The click is the user's own, so the handler starts it with source
 * `deeplink`, which spends no proactive budget.
 */
export function helpHref(path: string, target: HelpTarget): string {
  const value =
    target === 'hub'
      ? 'hub'
      : 'tour' in target
        ? `tour:${target.tour}${target.step ? `.${target.step}` : ''}`
        : `tip:${target.tip}`;
  const hashAt = path.indexOf('#');
  const hash = hashAt === -1 ? '' : path.slice(hashAt);
  const beforeHash = hashAt === -1 ? path : path.slice(0, hashAt);
  const queryAt = beforeHash.indexOf('?');
  const base = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const params = new URLSearchParams(queryAt === -1 ? '' : beforeHash.slice(queryAt + 1));
  params.delete('help');
  const rest = params.toString();
  // `value` is `[a-z0-9.:_-]` only — safe unencoded, and readable in a URL.
  return `${base}?${rest ? `${rest}&` : ''}help=${value}${hash}`;
}
